import { api } from '@/services/api';
import { ApiNotFoundError } from '@/services/apiError';
import type {
  SubmissionCreatePayload,
  SubmissionCreateResponse,
  SubmissionRecord,
  SubmissionResultSummary,
  SubmissionStatus,
} from '@/services/types';

interface RawSubmissionCreateResponse extends Omit<SubmissionCreateResponse, 'status'> {
  status: string;
}

interface RawSubmissionResultSummary extends Omit<SubmissionResultSummary, 'status'> {
  status: string;
}

interface RawSubmissionRecord extends Omit<SubmissionRecord, 'status' | 'result'> {
  status: string;
  result?: RawSubmissionResultSummary;
}

export async function createSubmission(payload: SubmissionCreatePayload) {
  const response = await api.post<RawSubmissionCreateResponse>('/submissions', payload);

  return {
    ...response.data,
    status: normalizeSubmissionStatus(response.data.status),
  };
}

export async function listSubmissions() {
  const response = await api.get<RawSubmissionRecord[]>('/submissions');
  return response.data.map((submission) => normalizeSubmissionRecord(submission));
}

export async function getSubmissionResult(submissionId: string) {
  const response = await api.get<RawSubmissionResultSummary>(`/submissions/${submissionId}/result`);
  return normalizeSubmissionResult(response.data);
}

export async function getSubmissionById(submissionId: string) {
  const submissions = await listSubmissions();
  const target = submissions.find((submission) => submission.id === submissionId);

  if (!target) {
    throw new ApiNotFoundError();
  }

  return target;
}

export async function getLatestSubmissionForProblem(problemId: string) {
  const response = await api.get<RawSubmissionRecord>(`/submissions/latest/problem/${problemId}`);
  return normalizeSubmissionRecord(response.data);
}

function normalizeSubmissionRecord(submission: RawSubmissionRecord): SubmissionRecord {
  return {
    ...submission,
    status: normalizeSubmissionStatus(submission.status),
    result: submission.result ? normalizeSubmissionResult(submission.result) : undefined,
  };
}

function normalizeSubmissionResult(result: RawSubmissionResultSummary): SubmissionResultSummary {
  return {
    ...result,
    status: normalizeSubmissionStatus(result.status),
    failures: Array.isArray(result.failures) ? result.failures : [],
  };
}

function normalizeSubmissionStatus(status: string): SubmissionStatus {
  switch (status.toUpperCase()) {
    case 'QUEUED':
      return 'QUEUED';
    case 'PENDING':
      return 'PENDING';
    case 'RUNNING':
      return 'RUNNING';
    case 'ACCEPTED':
      return 'ACCEPTED';
    case 'WRONG_ANSWER':
      return 'WRONG_ANSWER';
    case 'TIME_LIMIT_EXCEEDED':
      return 'TIME_LIMIT_EXCEEDED';
    case 'MEMORY_LIMIT_EXCEEDED':
      return 'MEMORY_LIMIT_EXCEEDED';
    case 'RUNTIME_ERROR':
      return 'RUNTIME_ERROR';
    case 'COMPILE_ERROR':
      return 'COMPILE_ERROR';
    default:
      return 'PENDING';
  }
}
